import { useQueryClient } from '@tanstack/react-query'
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  ChatbotLastQuestion,
  ChatbotMessage,
  ChatbotPromptResponse,
  ChatbotQuestion,
  ChatbotQuestionContext,
  ChatbotQuestionData,
  ChatbotQuestionsResponse,
} from './types'
import useChatbotVisible from '@hooks/useChatbot/useChatbotVisible'
import useChatbotLastQuestion from '@hooks/useChatbot/useChatbotLastQuestion'
import usePageMetadata from '@hooks/usePageMetadata'
import translate from '@i18n'
import useTracking from '@hooks/useTracking'
import {
  fetchCachedAnswer,
  fetchCachedQuestions,
  fetchQuestions,
  postPrompt,
  trackHandler,
} from './utils'
import useABTestValue from '@hooks/useABTestValue'

const ChatbotManager: FunctionComponent = () => {
  const queryClient = useQueryClient()
  const chatbotVisible = useChatbotVisible()
  const lastQuestion = useChatbotLastQuestion()
  const pageMetadata = usePageMetadata()
  const [articleId, setArticleId] = useState<string | undefined>()
  const handleTracking = useTracking(trackHandler)
  const isCUIEnabled = useABTestValue('showCUI') === 'show'

  const setNewMessage = useCallback(
    (newMessage: ChatbotMessage) => {
      queryClient.setQueryData(
        ['chatbotMessages'],
        (prevData: ChatbotMessage[]) =>
          prevData ? [...prevData, newMessage] : [newMessage]
      )
      queryClient.invalidateQueries({
        queryKey: ['chatbotMessages'],
      })
    },
    [queryClient]
  )

  const enrichQuestions = (
    questions: ChatbotQuestion[],
    context: ChatbotQuestionContext,
    isSuggestedQuestion: boolean
  ) =>
    questions.map((question) => ({
      context,
      isSuggestedQuestion,
      ...question,
    }))

  const getInitialQuestions = useCallback(async () => {
    queryClient.setQueryData(['chatbotInitialLoading'], true)
    queryClient.invalidateQueries({
      queryKey: ['chatbotInitialLoading'],
    })

    const result = await fetchCachedQuestions(articleId ? articleId : 'home')

    if (result?.ok) {
      const context = articleId || 'home'
      const { questions } = (await result?.json()) as ChatbotQuestionsResponse
      const enrichedQuestions: ChatbotQuestionData[] = enrichQuestions(
        questions,
        context,
        true
      )
      queryClient.setQueryData<ChatbotQuestionData[]>(
        ['chatbotInitialQuestions'],
        enrichedQuestions
      )
      queryClient.invalidateQueries({
        queryKey: ['chatbotInitialQuestions'],
      })
    }

    queryClient.setQueryData(['chatbotInitialLoading'], false)
    queryClient.invalidateQueries({
      queryKey: ['chatbotInitialLoading'],
    })
  }, [queryClient, articleId])

  const getAnswer = useMemo(
    () => async (question: ChatbotLastQuestion) => {
      setNewMessage({ type: 'question', text: question.data.text })
      queryClient.setQueryData(['chatbotAnswerLoading'], true)
      queryClient.invalidateQueries({
        queryKey: ['chatbotAnswerLoading'],
      })

      const promptResult = question.data.isCached
        ? await fetchCachedAnswer(question.data.id, question.data.context)
        : await postPrompt(
            question.data.text,
            question.data.isSuggestedQuestion,
            question.data.context,
            question.chatbotConversationId
          )
      if (promptResult?.ok) {
        let suggestedQuestions: ChatbotQuestionData[] = []
        const { content, links, traceId, spanId, conversationId } =
          (await promptResult?.json()) as ChatbotPromptResponse

        queryClient.setQueryData(['chatbotConversationId'], conversationId)
        queryClient.invalidateQueries({
          queryKey: ['chatbotConversationId'],
        })

        handleTracking({
          event: question.data.isCached
            ? 'chatbot_first_question'
            : 'chatbot_select_question',
          messageId: traceId,
          conversationId,
          isSuggestedQuestion: question.data.isSuggestedQuestion,
          questionAnswered: null,
        })

        const text = content?.map((contentItem) => contentItem.text).join(' ')
        const questionsResult = await fetchQuestions({
          text,
          context: question.data.context,
        })
        if (questionsResult?.ok) {
          const { questions } =
            (await questionsResult?.json()) as ChatbotQuestionsResponse
          const enrichedQuestions: ChatbotQuestionData[] = enrichQuestions(
            questions,
            question.data.context,
            true
          )
          suggestedQuestions = enrichedQuestions
        }

        setNewMessage({
          type: 'answer',
          content,
          links,
          ...(suggestedQuestions.length && { suggestedQuestions }),
          traceId,
          spanId,
          conversationId,
        })
      } else {
        setNewMessage({
          type: 'answer',
          content: [
            {
              text: translate('chatbot.answerMessage.error'),
            },
          ],
        })
      }

      queryClient.setQueryData(['chatbotAnswerLoading'], false)
      queryClient.invalidateQueries({
        queryKey: ['chatbotAnswerLoading'],
      })
    },
    [queryClient, setNewMessage, handleTracking]
  )

  useEffect(() => {
    if (
      isCUIEnabled &&
      !lastQuestion &&
      chatbotVisible &&
      !process.env.STORYBOOK
    ) {
      getInitialQuestions()
    }
  }, [getInitialQuestions, chatbotVisible, lastQuestion, isCUIEnabled])

  useEffect(() => {
    if (isCUIEnabled && lastQuestion && !process.env.STORYBOOK) {
      getAnswer(lastQuestion)
    }
  }, [lastQuestion, getAnswer, isCUIEnabled])

  useEffect(() => {
    if (isCUIEnabled) {
      setArticleId(pageMetadata?.id || undefined)
    }
  }, [pageMetadata?.id, isCUIEnabled])

  return null
}

export default ChatbotManager
